import React, { useMemo, useState } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js';
import { formatPriceWithCentsAndCommas } from 'lib/pricing/ui';
import { ColumnButton } from 'lib/components/ColumnButton';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { Product, PRODUCT_TO_NAME } from 'lib/enums/Product';
import { ProcessedProductsCategoryData } from 'lib/types/reports/ProductsTable';
import { capitalize } from 'lodash';
import { Permissions } from 'lib/permissions/roles';
import { useReportsLoading } from '../context/LoadingContext';
import { useReportParams } from '../context/ReportParamsContext';
import {
  fetchNoticesWithParams,
  fetchOrdersWithParams,
  fetchProductCategoryData
} from '../utils/elastic';
import { exportSearchableRecordsToCSV } from '../utils/exportOrdersToCSV';
import {
  NEWSPAPER_ORDER_CSV_COLUMNS,
  REPORT_V2_NOTICE_CSV_COLUMNS
} from '../constants/csvColumns';
import { REPORTS_V2_COLORS } from '../constants/colors';

Chart.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

enum Metric {
  Revenue = 'revenue',
  Volume = 'volume'
}

export default function ProductCategoryCharts() {
  const { withLoading } = useReportsLoading();
  const { getOrgIdsWithPermission, getOrgIds, getDateRange } =
    useReportParams();
  const { dateRange, startDate, endDate } = getDateRange();

  const orgIds = getOrgIds();
  const allowedOrgIds = getOrgIdsWithPermission(Permissions.PAYMENTS_REPORT);

  const ORDER_TYPE_TABS = [
    { label: PRODUCT_TO_NAME[Product.Notice].plural, value: Product.Notice },
    {
      label: PRODUCT_TO_NAME[Product.Obituary].plural,
      value: Product.Obituary
    },
    {
      label: PRODUCT_TO_NAME[Product.Classified].plural,
      value: Product.Classified
    }
  ];
  const METRIC_TABS = [
    { label: Metric.Revenue, value: Metric.Revenue },
    { label: Metric.Volume, value: Metric.Volume }
  ];
  const [selectedOrderType, setSelectedOrderType] = useState(Product.Notice);
  const [selectedMetric, setSelectedMetric] = useState(Metric.Revenue);

  const {
    value: processedData,
    isLoading,
    error
  } = useAsyncEffect<ProcessedProductsCategoryData[] | null>({
    fetchData: () =>
      withLoading(fetchProductCategoryData(allowedOrgIds, getDateRange)),
    initialData: null,
    dependencies: [orgIds.join(','), dateRange, startDate, endDate],
    errorConfig: {
      service: ColumnService.ELASTIC,
      message:
        '[COLUMN REPORTS] Failed to fetch product category aggregations for Products tab',
      tags: {
        orgsFilter: orgIds.join(', '),
        dateRange,
        startDateFilter: startDate || '',
        endDateFilter: endDate || ''
      }
    }
  });

  const filteredData = useMemo(() => {
    if (!processedData) {
      return [];
    }
    if (selectedOrderType === Product.Notice) {
      return processedData.filter(d => d.noticeTypeLabel);
    }
    return processedData.filter(d => d.orderType === selectedOrderType);
  }, [processedData, selectedOrderType]);

  const chartLabels = filteredData.map(
    (d: ProcessedProductsCategoryData) =>
      d.noticeTypeLabel || d.filingType || ''
  );
  const chartDataValues = filteredData.map((d: ProcessedProductsCategoryData) =>
    selectedMetric === Metric.Revenue ? d.revenue / 100 : d.volume
  );

  const FILINGTYPE_COLORS = Object.values(REPORTS_V2_COLORS);

  const pieChartData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          data: chartDataValues,
          backgroundColor: chartDataValues.map(
            (_, i) => FILINGTYPE_COLORS[i % FILINGTYPE_COLORS.length]
          ),
          borderWidth: 1
        }
      ]
    }),
    [chartLabels, chartDataValues]
  );

  const barChartData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          data: chartDataValues,
          backgroundColor: chartDataValues.map(
            (_, i) => FILINGTYPE_COLORS[i % FILINGTYPE_COLORS.length]
          ),
          borderWidth: 1,
          barThickness: 20
        }
      ]
    }),
    [chartLabels, chartDataValues, selectedMetric]
  );

  const totalMetric = useMemo(
    () =>
      filteredData.reduce(
        (sum: number, item: ProcessedProductsCategoryData) =>
          sum + item[selectedMetric as Metric],
        0
      ),
    [filteredData, selectedMetric]
  );

  const chartTooltipOptions = useMemo(
    () => ({
      plugins: {
        tooltip: {
          callbacks: {
            label(context: any) {
              let value: number;
              let percent = 0;
              let label = '';
              if (
                context.chart.config.type === 'pie' ||
                context.chart.config.type === 'doughnut'
              ) {
                value = context.parsed;
                const total = (context.dataset.data as number[]).reduce(
                  (a: number, b: number) => a + b,
                  0
                );
                percent = total ? (value / total) * 100 : 0;
                if (selectedMetric === Metric.Revenue) {
                  label = `${context.label}: $${Number(value).toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }
                  )} (${percent.toFixed(1)}%)`;
                } else {
                  label = `${context.label}: ${value} (${percent.toFixed(1)}%)`;
                }
              } else {
                value = context.parsed.x ?? 0;
                const { datasets } = context.chart.data;
                const total = datasets.reduce((sum: number, ds: any) => {
                  return (
                    sum +
                    ds.data.reduce(
                      (dsum: number, v: number) =>
                        dsum + (typeof v === 'number' ? v : 0),
                      0
                    )
                  );
                }, 0);
                percent = total ? (value / total) * 100 : 0;
                if (selectedMetric === Metric.Revenue) {
                  label = `$${Number(value).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} (${percent.toFixed(1)}%)`;
                } else {
                  label = `${value} (${percent.toFixed(1)}%)`;
                }
              }
              return label;
            }
          }
        }
      }
    }),
    [selectedMetric]
  );

  const handleExportToCSV = async () => {
    if (selectedOrderType === Product.Notice) {
      const notices = await fetchNoticesWithParams(
        allowedOrgIds,
        getDateRange,
        withLoading
      );

      exportSearchableRecordsToCSV({
        dataAndColumns: [
          {
            data: notices,
            columns: REPORT_V2_NOTICE_CSV_COLUMNS
          }
        ],
        filename: `product_category_report_notices_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      });
    } else {
      const newspaperOrders = await fetchOrdersWithParams(
        allowedOrgIds,
        getDateRange,
        withLoading
      );

      const filteredOrders = newspaperOrders.filter(
        o => o.ordertype === selectedOrderType
      );

      exportSearchableRecordsToCSV({
        dataAndColumns: [
          {
            data: filteredOrders,
            columns: NEWSPAPER_ORDER_CSV_COLUMNS
          }
        ],
        filename: `product_category_report_${selectedOrderType}_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div>
        Error: {error.message || 'An error occurred while loading the data'}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20
        }}
      >
        <div>
          <div className="flex gap-2">
            {ORDER_TYPE_TABS.map(tab => (
              <ColumnButton
                key={tab.value}
                buttonText={tab.label}
                onClick={() => setSelectedOrderType(tab.value)}
                type="button"
                secondary={selectedOrderType === tab.value}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex gap-2 ml-4">
            {METRIC_TABS.map(tab => (
              <ColumnButton
                buttonText={capitalize(tab.label)}
                key={tab.value}
                onClick={() => setSelectedMetric(tab.value)}
                type="button"
                secondary={selectedMetric === tab.value}
              />
            ))}
          </div>
          <div style={{ marginLeft: 10 }}></div>
          <ColumnButton
            buttonText="Export CSV"
            onClick={handleExportToCSV}
            type="button"
            primary
          />
        </div>
      </div>
      {!filteredData.length ? (
        <div>No data available</div>
      ) : (
        <>
          <div className="flex justify-between items-start">
            <div className="flex flex-col w-[400px] min-w-[300px] h-[200px]">
              <h2 className="font-semibold mb-2">
                {selectedMetric === Metric.Revenue
                  ? capitalize(Metric.Revenue)
                  : capitalize(Metric.Volume)}{' '}
                by{' '}
                {selectedOrderType === Product.Notice
                  ? 'Notice Type'
                  : 'Filing Type'}
              </h2>
              {filteredData.length ? (
                <Pie
                  data={pieChartData}
                  options={{
                    ...chartTooltipOptions,
                    radius: '80%',
                    plugins: {
                      ...chartTooltipOptions.plugins,
                      legend: { display: false }
                    }
                  }}
                  width={400}
                  height={250}
                />
              ) : (
                <div className="min-h-[250px] flex items-center justify-center">
                  No data available
                </div>
              )}
            </div>
            <div className="flex flex-col w-[600px] min-w-[600px] h-[300px]">
              <div className="flex justify-between items-center mb-2">
                <h2 className="font-semibold">
                  {selectedMetric === Metric.Revenue
                    ? capitalize(Metric.Revenue)
                    : capitalize(Metric.Volume)}{' '}
                  by{' '}
                  {selectedOrderType === Product.Notice
                    ? 'Notice Type'
                    : 'Filing Type'}
                </h2>
                <div className="text-m">
                  Total{' '}
                  {selectedMetric === Metric.Revenue
                    ? Metric.Revenue
                    : Metric.Volume}
                  :{' '}
                  {selectedMetric === Metric.Revenue
                    ? formatPriceWithCentsAndCommas('$', totalMetric)
                    : totalMetric}
                </div>
              </div>

              {filteredData.length ? (
                <div
                  className="flex-1 overflow-y-auto"
                  style={{
                    minHeight: '300px',
                    maxHeight: '400px'
                  }}
                >
                  <div
                    style={{
                      height: Math.max(300, filteredData.length * 50),
                      minWidth: '600px'
                    }}
                  >
                    <Bar
                      data={barChartData}
                      options={{
                        ...chartTooltipOptions,
                        indexAxis: 'y',
                        plugins: {
                          ...chartTooltipOptions.plugins,
                          legend: { display: false }
                        },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          x: {
                            position: 'top'
                          }
                        }
                      }}
                      width={600}
                    />
                  </div>
                </div>
              ) : (
                <div className="min-h-[300px] flex items-center justify-center">
                  No data available
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
